import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Clock, Shield, Sparkles, Home, Building2, Star, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const serviceData = {
  residential: {
    title: "Residential Cleaning",
    tagline: "A Clean Home, A Happy Life",
    description: "Our residential cleaning services are designed to give you back your time while ensuring your home is spotless, healthy, and welcoming. We use eco-friendly products safe for your family and pets.",
    image: "https://images.unsplash.com/photo-1708113388262-17fdf0e21205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzcG90bGVzcyUyMGx1eHVyeSUyMGxpdmluZyUyMHJvb20lMjBzdW5saWdodCUyMGludGVyaW9yJTIwZGVzaWdufGVufDB8fHx8MTc3MzM1NTEyOXww&ixlib=rb-4.1.0&q=85",
    startingPrice: 79,
    services: [
      {
        name: "Regular Maintenance Cleaning",
        description: "Keep your home consistently clean with our weekly or bi-weekly cleaning service.",
        includes: [
          "Dusting all surfaces, furniture, and decor",
          "Vacuuming carpets and mopping hard floors",
          "Kitchen cleaning including countertops, appliances, and sink",
          "Bathroom sanitization and disinfection",
          "Making beds and tidying rooms",
          "Emptying trash and replacing liners"
        ],
        price: "Starting at $79"
      },
      {
        name: "Deep Cleaning",
        description: "A thorough top-to-bottom clean for homes that need extra attention.",
        includes: [
          "Everything in regular cleaning PLUS:",
          "Inside cabinet and drawer cleaning",
          "Baseboard and crown molding dusting",
          "Light fixture and ceiling fan cleaning",
          "Window sill and track cleaning",
          "Behind and under furniture cleaning",
          "Vent and register dusting",
          "Door and door frame wiping"
        ],
        price: "Starting at $149"
      },
      {
        name: "Move-In / Move-Out Cleaning",
        description: "Perfect for tenants, landlords, and new homeowners. Get your deposit back or start fresh.",
        includes: [
          "Complete deep cleaning of all rooms",
          "Inside all cabinets, closets, and drawers",
          "Inside refrigerator and oven cleaning",
          "Interior window cleaning",
          "Garage sweeping (if applicable)",
          "Light switch and outlet cover sanitization",
          "Wall spot cleaning",
          "Final walkthrough inspection"
        ],
        price: "Starting at $199"
      },
      {
        name: "Post-Construction Cleaning",
        description: "Remove dust, debris, and residue left behind after renovations or new construction.",
        includes: [
          "Construction dust and debris removal",
          "Paint splatter and sticker removal",
          "Window and glass cleaning",
          "Cabinet interior and exterior cleaning",
          "Floor scrubbing and polishing",
          "Vent cleaning and dusting",
          "Final detail cleaning",
          "Touch-up cleaning available"
        ],
        price: "Starting at $249"
      },
      {
        name: "Seasonal Deep Clean",
        description: "Give your home a refresh each season with our comprehensive seasonal cleaning.",
        includes: [
          "Full deep cleaning service",
          "Window washing (interior)",
          "Patio/deck sweeping",
          "Outdoor furniture wipe-down",
          "Closet organization assistance",
          "Mattress vacuuming",
          "Upholstery vacuuming",
          "Air freshening throughout"
        ],
        price: "Starting at $179"
      }
    ],
    benefits: [
      "Flexible scheduling - weekly, bi-weekly, or monthly",
      "Same trusted cleaners every visit",
      "Eco-friendly, non-toxic cleaning products",
      "100% satisfaction guarantee",
      "Fully insured and bonded staff",
      "Online booking and payment"
    ]
  },
  commercial: {
    title: "Commercial Cleaning",
    tagline: "Professional Spaces Deserve Professional Care",
    description: "Create a healthier, more productive work environment with our commercial cleaning services. We work around your schedule to minimize disruption while maximizing cleanliness.",
    image: "https://images.unsplash.com/photo-1758630737900-a28682c5aa69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3NzMzNTUxMzB8MA&ixlib=rb-4.1.0&q=85",
    startingPrice: 149,
    services: [
      {
        name: "Office Cleaning",
        description: "Daily, weekly, or custom schedule cleaning for offices of all sizes.",
        includes: [
          "Workstation and desk sanitization",
          "Common area and lobby cleaning",
          "Kitchen/break room cleaning and sanitization",
          "Restroom deep cleaning and restocking",
          "Trash removal and recycling",
          "Floor vacuuming and mopping",
          "Glass and mirror cleaning",
          "Conference room preparation"
        ],
        price: "Starting at $149"
      },
      {
        name: "Retail Store Cleaning",
        description: "Keep your retail space inviting for customers with our specialized retail cleaning.",
        includes: [
          "Sales floor cleaning and maintenance",
          "Display case and shelf dusting",
          "Fitting room sanitization",
          "Entrance and storefront cleaning",
          "Checkout counter disinfection",
          "Stockroom organization and cleaning",
          "Floor care (tile, carpet, hardwood)",
          "Window and glass cleaning"
        ],
        price: "Starting at $129"
      },
      {
        name: "Medical & Dental Office Cleaning",
        description: "Healthcare-grade cleaning and sanitization for medical facilities.",
        includes: [
          "EPA-approved disinfectant application",
          "Waiting room and reception sanitization",
          "Exam room deep cleaning",
          "Medical-grade waste disposal compliance",
          "High-touch surface disinfection",
          "Floor sanitization and maintenance",
          "Restroom hospital-grade cleaning",
          "HIPAA-compliant practices"
        ],
        price: "Starting at $199"
      },
      {
        name: "Restaurant & Food Service Cleaning",
        description: "Meet health codes and impress customers with our food service cleaning.",
        includes: [
          "Kitchen deep cleaning and degreasing",
          "Dining area sanitization",
          "Restroom cleaning and restocking",
          "Floor degreasing and sanitization",
          "Hood and exhaust cleaning",
          "Walk-in cooler/freezer cleaning",
          "Trash area sanitization",
          "Health inspection preparation"
        ],
        price: "Starting at $179"
      },
      {
        name: "Industrial & Warehouse Cleaning",
        description: "Large-scale cleaning solutions for warehouses, factories, and industrial spaces.",
        includes: [
          "Floor sweeping and scrubbing",
          "High dusting and rafter cleaning",
          "Loading dock cleaning",
          "Office area within facility",
          "Restroom and break room cleaning",
          "Trash and debris removal",
          "Equipment exterior cleaning",
          "Safety compliance cleaning"
        ],
        price: "Starting at $299"
      },
      {
        name: "Post-Event Cleaning",
        description: "Quick turnaround cleaning after corporate events, parties, or conferences.",
        includes: [
          "Trash and debris removal",
          "Table and chair cleaning",
          "Floor cleaning and stain treatment",
          "Restroom restoration",
          "Kitchen/catering area cleaning",
          "Decoration and setup removal",
          "Venue restoration to original state",
          "Same-day or next-day service"
        ],
        price: "Starting at $249"
      }
    ],
    benefits: [
      "After-hours cleaning available",
      "Customized cleaning schedules",
      "Background-checked, professional staff",
      "Green cleaning options available",
      "Detailed quality inspections",
      "Dedicated account manager"
    ]
  },
  specialized: {
    title: "Specialized Services",
    tagline: "Expert Solutions for Specific Cleaning Needs",
    description: "Some cleaning jobs require special expertise, equipment, and attention. Our specialized services address those unique needs with professional-grade solutions.",
    image: "https://images.unsplash.com/photo-1737092720524-9140dc372efb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxzaGlueSUyMGhhcmR3b29kJTIwZmxvb3IlMjBjbGVhbiUyMHBvbGlzaCUyMGhvbWV8ZW58MHx8fHwxNzczMzU1MTQzfDA&ixlib=rb-4.1.0&q=85",
    startingPrice: 99,
    services: [
      {
        name: "Carpet Cleaning",
        description: "Professional deep carpet cleaning to remove stains, odors, and allergens.",
        includes: [
          "Pre-treatment of stains and high-traffic areas",
          "Hot water extraction (steam cleaning)",
          "Spot and stain removal treatment",
          "Deodorizing and freshening",
          "Carpet grooming for faster drying",
          "Furniture moving (upon request)",
          "Scotchgard protection (optional)",
          "Fast-dry technology available"
        ],
        price: "Starting at $99 per room"
      },
      {
        name: "Upholstery Cleaning",
        description: "Revive your furniture with professional upholstery cleaning services.",
        includes: [
          "Fabric type assessment",
          "Pre-treatment of stains",
          "Deep extraction cleaning",
          "Odor elimination",
          "Fabric protection application",
          "Cushion and pillow cleaning",
          "Leather conditioning (for leather items)",
          "Quick-dry process"
        ],
        price: "Starting at $79 per piece"
      },
      {
        name: "Window Cleaning",
        description: "Crystal-clear windows inside and out for homes and businesses.",
        includes: [
          "Interior window cleaning",
          "Exterior window cleaning",
          "Screen cleaning and inspection",
          "Window track and sill cleaning",
          "Hard water stain removal",
          "Storm window cleaning",
          "Skylight cleaning",
          "Mirror cleaning included"
        ],
        price: "Starting at $5 per window"
      },
      {
        name: "Pressure Washing",
        description: "Restore the beauty of your exterior surfaces with professional pressure washing.",
        includes: [
          "Driveway and walkway cleaning",
          "Deck and patio restoration",
          "Siding and exterior wall cleaning",
          "Fence cleaning and brightening",
          "Garage floor cleaning",
          "Gutter exterior cleaning",
          "Concrete stain removal",
          "Eco-friendly cleaning agents"
        ],
        price: "Starting at $149"
      },
      {
        name: "Tile & Grout Cleaning",
        description: "Restore your tile floors and walls to their original beauty.",
        includes: [
          "Deep grout line cleaning",
          "Tile surface restoration",
          "Grout sealing and protection",
          "Mold and mildew treatment",
          "Color restoration (if needed)",
          "High-pressure steam cleaning",
          "Kitchen backsplash cleaning",
          "Bathroom tile restoration"
        ],
        price: "Starting at $1.50 per sq ft"
      },
      {
        name: "Mattress Cleaning",
        description: "Eliminate dust mites, allergens, and odors from your mattresses.",
        includes: [
          "Deep vacuuming and dust removal",
          "UV-C sanitization treatment",
          "Stain spot treatment",
          "Deodorizing and freshening",
          "Allergen reduction treatment",
          "Dust mite elimination",
          "Quick-dry process",
          "Pillow cleaning available"
        ],
        price: "Starting at $69 per mattress"
      },
      {
        name: "Air Duct Cleaning",
        description: "Improve indoor air quality with professional air duct cleaning.",
        includes: [
          "Complete duct system inspection",
          "High-powered vacuum extraction",
          "Vent and register cleaning",
          "Sanitization treatment",
          "Debris and dust removal",
          "Before and after documentation",
          "Dryer vent cleaning (optional)",
          "HVAC efficiency improvement"
        ],
        price: "Starting at $299"
      },
      {
        name: "Hoarding & Estate Cleanout",
        description: "Compassionate, thorough cleaning for hoarding situations and estate cleanouts.",
        includes: [
          "Sensitive, non-judgmental approach",
          "Sorting and organization",
          "Donation coordination",
          "Disposal and hauling",
          "Deep cleaning after cleanout",
          "Biohazard handling (if needed)",
          "Property preparation for sale",
          "Flexible timeline available"
        ],
        price: "Custom quote required"
      }
    ],
    benefits: [
      "State-of-the-art equipment",
      "Trained and certified technicians",
      "Eco-friendly solutions available",
      "Satisfaction guaranteed",
      "Free estimates",
      "Emergency services available"
    ]
  }
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = serviceData[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Service Not Found</h1>
          <Link to="/" className="text-[#2563EB] hover:underline mt-4 inline-block">
            Return to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const scrollToBooking = () => {
    window.location.href = "/#booking";
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`service-page-${serviceId}`}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#1D4ED8] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-4">
                {service.title}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
                {service.tagline}
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                {service.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToBooking}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="book-now-btn"
                >
                  Book Now - Starting at ${service.startingPrice}
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-200 rounded-full px-8 py-6 text-lg"
                  onClick={() => window.location.href = "tel:506-210-3395"}
                  data-testid="call-now-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call 506-210-3395
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[400px] object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">100% Satisfaction</p>
                    <p className="text-sm text-slate-500">Guaranteed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
              What We Offer
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
              Our {service.title} Services
            </h2>
          </div>

          <div className="space-y-8">
            {service.services.map((item, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-3xl p-6 md:p-8 hover:shadow-lg transition-shadow"
                data-testid={`service-item-${index}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                      <h3 className="text-2xl font-bold text-slate-900">
                        {item.name}
                      </h3>
                      <span className="bg-[#2563EB] text-white px-4 py-2 rounded-full font-semibold">
                        {item.price}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-6">{item.description}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {item.includes.map((feature, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-8">
                The River City Cleaners Difference
              </h2>
              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#2563EB]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-slate-600 mb-6">
                Book your {service.title.toLowerCase()} service today and experience the River City Cleaners difference.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-slate-700">
                  <Clock className="w-5 h-5 text-[#2563EB]" />
                  <span>Quick response within 24 hours</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Star className="w-5 h-5 text-[#2563EB]" />
                  <span>4.9 star rating from 500+ clients</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Shield className="w-5 h-5 text-[#2563EB]" />
                  <span>Fully insured and bonded</span>
                </div>
              </div>
              <Button
                onClick={scrollToBooking}
                className="w-full mt-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full py-6 text-lg font-semibold"
                data-testid="cta-book-btn"
              >
                Get Your Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
